import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const SecurityHeader = ({ 
  isCollapsed = false, 
  onToggleSidebar = () => {},
  className = "" 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [user] = useState({
    name: 'Admin User',
    role: 'Security Administrator',
    avatar: null
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Simulate WebSocket connection status
    const statusInterval = setInterval(() => {
      const statuses = ['connected', 'connecting', 'disconnected'];
      const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
      setConnectionStatus(randomStatus);
    }, 30000);

    return () => {
      clearInterval(timer);
      clearInterval(statusInterval);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'text-success';
      case 'connecting':
        return 'text-warning';
      case 'disconnected':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Wifi';
      case 'connecting':
        return 'WifiOff';
      case 'disconnected':
        return 'WifiOff';
      default:
        return 'Wifi';
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-card border-b border-border security-shadow-card ${className}`}>
      <div className="flex items-center justify-between h-16 px-4">
        {/* Left Section - Logo and Brand */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
              <Icon name="Shield" size={20} className="text-primary-foreground" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground">SecureLock</h1>
              <p className="text-xs text-muted-foreground">Admin Dashboard</p>
            </div>
          </div>
        </div>

        {/* Center Section - Connection Status and Time */}
        <div className="hidden md:flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <Icon 
              name={getConnectionStatusIcon()} 
              size={16} 
              className={`${getConnectionStatusColor()} ${connectionStatus === 'connected' ? 'security-pulse' : ''}`}
            />
            <span className={`text-sm font-medium ${getConnectionStatusColor()}`}>
              {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
            </span>
          </div>
          
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Clock" size={16} />
            <div className="text-sm">
              <span className="font-mono">{formatTime(currentTime)}</span>
              <span className="ml-2">{formatDate(currentTime)}</span>
            </div>
          </div>
        </div>

        {/* Right Section - User Info and Actions */}
        <div className="flex items-center space-x-3">
          {/* Mobile Connection Status */}
          <div className="md:hidden">
            <Icon 
              name={getConnectionStatusIcon()} 
              size={20} 
              className={`${getConnectionStatusColor()} ${connectionStatus === 'connected' ? 'security-pulse' : ''}`}
            />
          </div>

          {/* User Info */}
          <div className="hidden sm:flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm font-medium text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.role}</p>
            </div>
            <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
              <Icon name="User" size={16} className="text-secondary-foreground" />
            </div>
          </div>

          {/* Mobile User Avatar */}
          <div className="sm:hidden w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
            <Icon name="User" size={16} className="text-secondary-foreground" />
          </div>

          {/* Logout Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            iconName="LogOut"
            className="text-muted-foreground hover:text-foreground security-transition"
          >
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default SecurityHeader;